import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";

// Errors persist until dismissed (owner item 15); everything else auto-dismisses after ~5 s.
const _err = toast.error.bind(toast);
toast.error = (msg, opts = {}) => _err(msg, { duration: Infinity, ...opts });
import "./index.css";
import { AuthProvider, useAuth } from "./lib/api";
import Shell from "./components/Shell";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clusters from "./pages/Clusters";
import ClusterDetail from "./pages/ClusterDetail";
import AddCluster from "./pages/AddCluster";
import Docs from "./pages/Docs";
import Models from "./pages/Models";
import ModelDetail from "./pages/ModelDetail";
import UseCases from "./pages/UseCases";
import UseCaseDetail from "./pages/UseCaseDetail";
import UseCaseForm from "./pages/UseCaseForm";
import EndpointCreate from "./pages/EndpointCreate";
import Deploy from "./pages/Deploy";
import Deployments from "./pages/Deployments";
import DeploymentDetail from "./pages/DeploymentDetail";
import APIKeys, { KeyDetail } from "./pages/APIKeys";
import EndpointDetail from "./pages/EndpointDetail";
import Usage from "./pages/Usage";
import Playground from "./pages/Playground";
import Settings from "./pages/Settings";
import Machines from "./pages/Machines";
import AddMachine from "./pages/machines/AddMachine";
import MachineDetail from "./pages/machines/MachineDetail";
import Cloud from "./pages/Cloud";
import RentGpu from "./pages/cloud/RentGpu";
import AccountForm from "./pages/cloud/AccountForm";
import InstanceDetail from "./pages/cloud/InstanceDetail";
import ChangePassword from "./pages/ChangePassword";
import Endpoints from "./pages/Endpoints";

function Protected({ children, roles }) {
  const { loading, user } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-cx-muted text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

const INFRA = ["admin", "operator"];
const RedirectMachine = () => { const { id } = useParams(); return <Navigate to={`/machines/${id}`} replace />; };

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route element={<Protected><Shell /></Protected>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {/* ① Infrastructure */}
            <Route path="/clusters" element={<Protected roles={INFRA}><Clusters /></Protected>} />
            <Route path="/clusters/new" element={<Protected roles={["admin"]}><AddCluster /></Protected>} />
            <Route path="/clusters/:id" element={<Protected roles={INFRA}><ClusterDetail /></Protected>} />
            <Route path="/machines" element={<Protected roles={INFRA}><Machines /></Protected>} />
            <Route path="/machines/new" element={<Protected roles={INFRA}><AddMachine /></Protected>} />
            <Route path="/machines/:id" element={<Protected roles={INFRA}><MachineDetail /></Protected>} />
            <Route path="/cloud" element={<Protected roles={INFRA}><Cloud /></Protected>} />
            <Route path="/cloud/new-instance" element={<Protected roles={INFRA}><RentGpu /></Protected>} />
            <Route path="/cloud/accounts/new" element={<Protected roles={["admin"]}><AccountForm /></Protected>} />
            <Route path="/cloud/accounts/:id" element={<Protected roles={INFRA}><AccountForm /></Protected>} />
            <Route path="/cloud/instances/:id" element={<Protected roles={INFRA}><InstanceDetail /></Protected>} />
            {/* ②③ AI workloads (existing pages mounted under the new paths until their phases) */}
            <Route path="/use-cases" element={<UseCases />} />
            <Route path="/use-cases/new" element={<UseCaseForm />} />
            <Route path="/use-cases/:id/edit" element={<UseCaseForm />} />
            <Route path="/use-cases/:id" element={<UseCaseDetail />} />
            <Route path="/models" element={<Models />} />
            <Route path="/models/:id" element={<ModelDetail />} />
            <Route path="/deployments" element={<Deployments />} />
            <Route path="/deployments/new" element={<Deploy />} />
            <Route path="/deployments/:id" element={<DeploymentDetail />} />
            {/* ④ Serving */}
            <Route path="/endpoints" element={<Endpoints />} />
            <Route path="/endpoints/new" element={<Protected roles={["admin", "operator", "developer"]}><EndpointCreate /></Protected>} />
            <Route path="/endpoints/:id" element={<EndpointDetail />} />
            <Route path="/keys" element={<Protected roles={["admin", "operator", "developer"]}><APIKeys /></Protected>} />
            <Route path="/keys/:id" element={<Protected roles={["admin", "operator", "developer"]}><KeyDetail /></Protected>} />
            <Route path="/playground" element={<Playground />} />
            {/* ⑤ Insights */}
            <Route path="/usage" element={<Usage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/docs/:slug" element={<Docs />} />
            <Route path="/docs/:slug/:sub" element={<Docs />} />
            {/* Old paths → new homes */}
            <Route path="/welcome" element={<Navigate to="/dashboard" replace />} />
            <Route path="/deploy" element={<Navigate to="/deployments/new" replace />} />
            <Route path="/compute/machines" element={<Navigate to="/machines" replace />} />
            <Route path="/compute/machines/:id" element={<RedirectMachine />} />
            <Route path="/compute/providers" element={<Navigate to="/cloud?tab=accounts" replace />} />
            <Route path="/developer/playground" element={<Navigate to="/playground" replace />} />
            <Route path="/developer/api-keys" element={<Navigate to="/keys" replace />} />
            <Route path="/api-keys" element={<Navigate to="/keys" replace />} />
            <Route path="/observe/usage" element={<Navigate to="/usage" replace />} />
            <Route path="/settings/integrations" element={<Navigate to="/settings?tab=integrations" replace />} />
            <Route path="/settings/access" element={<Navigate to="/keys?tab=policy" replace />} />
            <Route path="/admin/assistant" element={<Navigate to="/settings?tab=integrations" replace />} />
            <Route path="/admin/tenants" element={<Navigate to="/settings?tab=workspace" replace />} />
            <Route path="/admin/users" element={<Navigate to="/settings?tab=workspace" replace />} />
            <Route path="/admin/pricing" element={<Navigate to="/settings?tab=pricing" replace />} />
            {/* Removed from the sidebar (reachable by URL until Phase 5 cleanup) */}
            <Route path="/developer/routes" element={<Navigate to="/endpoints" replace />} />
            <Route path="/marketplace" element={<Navigate to="/usage?tab=budgets" replace />} />
            <Route path="/marketplace/*" element={<Navigate to="/usage?tab=budgets" replace />} />
            <Route path="/evaluations" element={<Navigate to="/models" replace />} />
            <Route path="/observe/monitoring" element={<Navigate to="/dashboard?tab=capacity" replace />} />
            <Route path="/observe/alerts" element={<Navigate to="/dashboard" replace />} />
            <Route path="/observe/requests" element={<Navigate to="/usage?tab=breakdown" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" visibleToasts={3} duration={5000} closeButton expand={false} gap={8} offset={16} toastOptions={{ className: "cx-toast", unstyled: false }} />
    </AuthProvider>
  );
}

createRoot(document.getElementById("root")).render(<App />);
